import React from 'react';
import { getIntl } from '../../../../utils/intl';
import Stack, { FormStack } from '../../../v2BaseComponents/Stack';
import { SplitFormRow } from '../../../v2BaseComponents/Form';
import { Field } from 'formik';
import { FormikInputs } from '../../../v2BaseComponents/Inputs';
import { required } from '../../../../utils/validators';
import { ABAC, APITypes, OrganizationTypes } from '../../../../primary-record-types';
import CheckboxesField from './CheckboxesField';
import FilesField from './FilesField';
import Text from '../../../v2BaseComponents/Text';
import { defaultInvitationDefinitionPermissions, noAccessAppPermissions } from '../../../../utils/defaultPermissions';
import { isConsentCheckboxInput, isFileInput } from '../../../../hooks/api/useOrganizationInvitationDefinitions';
import FileInputsField from './FileInputsField';

const intl = getIntl('invitation-definition-form-fields', {
    name: 'Name',
    message: 'Message',
    requestedProfilePermissionsSubText:
        '* Select permissions to request from the client.  The client ultimately decides which permissions to grant to your organization.',
});

export type FormValue = {
    name: string;
    message: string;
    requestedProfilePermissions: ABAC.AppPermissions;
    checkboxInputs: OrganizationTypes.InvitationDefinitionInputConsentCheckbox[];
    fileInputs: OrganizationTypes.InvitationDefinitionInputFile[];
    files: APITypes.organizationService.InvitationDefinitionFileUpsert[];
};

export type FormFieldsOwnProps = {
    isDisabled: boolean;
    value: FormValue;
    initialValues: FormValue;
};

export type FormFieldsProps = FormFieldsOwnProps;

export const mapInvitationDefinitionToFormValues = (
    invitationDefinition: APITypes.organizationService.GetInvitationDefinitionResponse,
): FormValue => {
    return {
        name: invitationDefinition.name,
        message: invitationDefinition.message || '',
        requestedProfilePermissions: invitationDefinition.requestedProfilePermissions || noAccessAppPermissions,
        checkboxInputs: invitationDefinition.inputs.filter(isConsentCheckboxInput),
        fileInputs: invitationDefinition.inputs.filter(isFileInput),
        files: invitationDefinition.files,
    };
};

export const defaultFormValues: FormValue = {
    name: '',
    message: '',
    requestedProfilePermissions: defaultInvitationDefinitionPermissions,
    checkboxInputs: [],
    fileInputs: [],
    files: [],
};

export const mapFormValuesToRequest = (
    values: FormValue,
):
    | APITypes.organizationService.CreateInvitationDefinitionRequest
    | APITypes.organizationService.UpdateInvitationDefinitionRequest => {
    return {
        name: values.name,
        message: values.message,
        requestedProfilePermissions: values.requestedProfilePermissions,
        inputs: [...values.checkboxInputs, ...values.fileInputs],
        files: values.files.map((file) => ({
            ...file,
            tempFileId: file.fileId ? undefined : file.tempFileId,
            fileId: file.fileId || undefined,
        })),
    };
};

const FormFields: React.FC<FormFieldsProps> = (props) => {
    return (
        <FormStack>
            <SplitFormRow>
                <Field name="name" title={intl('name')} component={FormikInputs.Input} isRequired validate={required} />
                <Field name="message" title={intl('message')} component={FormikInputs.TextArea} />
                <Stack vertical fullWidth align="flex-start">
                    <Field
                        name="requestedProfilePermissions"
                        title="Requested Profile Permissions"
                        component={FormikInputs.PermissionsInput}
                        initialAppPermissions={props.initialValues.requestedProfilePermissions}
                    />
                    <Text variant="caption-1">{intl('requestedProfilePermissionsSubText')}</Text>
                </Stack>
                <FilesField isDisabled={props.isDisabled} values={props.value.files} />
                <CheckboxesField isDisabled={props.isDisabled} values={props.value.checkboxInputs} />
                <FileInputsField isDisabled={props.isDisabled} values={props.value.fileInputs} />
            </SplitFormRow>
        </FormStack>
    );
};

export default React.memo(FormFields);
